import React, { useContext } from 'react';
import { AreaChart, Area, XAxis, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { ThemeContext } from '../../context/ThemeContext';
import SpotlightCard from '../SpotlightCard';
import { formatMoney } from '../../utils/money';
import './ProductPreview.css';

function layoutTokens(variant) {
  const isSwap = variant === 'swap';
  return {
    isSwap,
    header: isSwap ? 'px-5 py-3.5' : 'px-4 py-3',
    body: isSwap ? 'p-5 space-y-3' : 'p-3 sm:p-4 space-y-3',
    section: isSwap ? 'px-5 py-3' : 'px-3 py-2.5',
    row: isSwap ? 'px-5 py-3' : 'px-4 py-2.5',
    kpi: isSwap ? 'px-4 py-3' : 'px-3 py-2.5',
    footer: isSwap ? 'px-5 py-2.5' : 'px-4 py-2',
  };
}

function PreviewShell({ modoOscuro, variant, className, children }) {
  const bg = modoOscuro ? 'bg-gray-900' : 'bg-white';

  if (variant === 'swap') {
    return (
      <div className={`product-preview-swap ${bg} h-full flex flex-col ${className}`}>
        <div className="product-preview-swap__scroll flex flex-col min-h-0">
          {children}
        </div>
      </div>
    );
  }

  const rounded = variant === 'panel' ? 'rounded-2xl' : 'rounded-3xl';
  const border = modoOscuro ? 'border-gray-700' : 'border-gray-200';
  const shadow = variant === 'panel' ? 'shadow-xl' : 'shadow-2xl';
  return (
    <SpotlightCard spotlightColor="transparent" className={`${bg} ${rounded} border ${border} ${shadow} overflow-hidden h-full ${className}`}>
      {children}
    </SpotlightCard>
  );
}

/** Datos demo coherentes: pendiente = ventas − cobrado, flujo = cobrado − egresos */
const DEMO = {
  ventas: 612000,
  cobrado: 498400,
  pendiente: 113600,
  egresos: 70050,
  flujo: 428350,
  tasaCobro: 81,
  dso: 18,
  margenPct: 32,
  margenMonto: 195840,
  impagas: 6,
  vencidas: 4,
  promesasVencidas: 2,
};

const EVOLUCION = [
  { mes: 'Abr', ventas: 318000 },
  { mes: 'May', ventas: 352000 },
  { mes: 'Jun', ventas: 389000 },
  { mes: 'Jul', ventas: 421000 },
  { mes: 'Ago', ventas: 465000 },
  { mes: 'Sep', ventas: 612000 },
];

const COBRANZA_TREND = [
  { mes: 'Abr', ventas: 72 },
  { mes: 'May', ventas: 75 },
  { mes: 'Jun', ventas: 71 },
  { mes: 'Jul', ventas: 78 },
  { mes: 'Ago', ventas: 79 },
  { mes: 'Sep', ventas: 81 },
];

const COBRANZA_PIE = [
  { nombre: 'Cobrado', valor: DEMO.cobrado, color: '#1F80FF' },
  { nombre: 'Pendiente', valor: DEMO.pendiente, color: '#F59E42' },
];

const COBRAR_ROWS = [
  { numero: '1042', cliente: 'López SRL', saldo: 45200, aging: 'Vencida', tone: 'red' },
  { numero: '1038', cliente: 'Martínez', saldo: 35600, aging: '1–30 días', tone: 'amber' },
  { numero: '1035', cliente: 'García', saldo: 32800, aging: 'Vencida', tone: 'red' },
];

const AGING_BUCKETS = [
  { label: '1–30 días', count: 1, monto: 35600, color: 'bg-amber-400' },
  { label: '31–60 días', count: 1, monto: 12000, color: 'bg-orange-500' },
  { label: '61+ días', count: 4, monto: 66000, color: 'bg-red-500' },
];

const PROYECCION = [
  { mes: 'Oct', ingresos: 520000, egresos: 72000, flujo: 448000, acumulado: 448000 },
  { mes: 'Nov', ingresos: 535000, egresos: 73500, flujo: 461500, acumulado: 909500 },
  { mes: 'Dic', ingresos: 548000, egresos: 75000, flujo: 473000, acumulado: 1382500 },
];

export const PRODUCT_PREVIEW_PRESETS = {
  dashboard: 'dashboard',
  cobrar: 'cobrar',
  analisis: 'analisis',
  contador: 'contador',
};

function moneyShort(value) {
  return formatMoney(value).replace(',00', '');
}

function toneClass(tone, modoOscuro) {
  const tones = {
    default: modoOscuro ? 'text-white' : 'text-gray-900',
    green: modoOscuro ? 'text-green-300' : 'text-green-700',
    amber: modoOscuro ? 'text-amber-300' : 'text-amber-700',
    red: modoOscuro ? 'text-red-300' : 'text-red-700',
    blue: 'text-[#1F80FF]',
  };
  return tones[tone] || tones.default;
}

function agingBadgeClass(tone, modoOscuro) {
  if (tone === 'red') return modoOscuro ? 'bg-red-900/50 text-red-200' : 'bg-red-50 text-red-700';
  if (tone === 'amber') return modoOscuro ? 'bg-amber-900/50 text-amber-200' : 'bg-amber-50 text-amber-800';
  return modoOscuro ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700';
}

function Sparkline({ data, height, gradientId, modoOscuro }) {
  const chartTick = modoOscuro ? '#9CA3AF' : '#6B7280';
  return (
    <div className="w-full pointer-events-none" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 8, left: 4, bottom: 2 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#1F80FF" stopOpacity={0.35} />
              <stop offset="95%" stopColor="#1F80FF" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="mes" axisLine={false} tickLine={false} tick={{ fill: chartTick, fontSize: 10 }} dy={2} />
          <Area type="monotone" dataKey="ventas" stroke="#1F80FF" strokeWidth={2} fill={`url(#${gradientId})`} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function PieCobranza({ modoOscuro, textPri, textMuted, panelClass, compact }) {
  const size = compact ? 56 : 64;
  return (
    <div className={`rounded-xl border p-2.5 ${panelClass}`}>
      <p className={`text-xs font-semibold mb-1 ${textPri}`}>Análisis de cobranza</p>
      <div className="flex items-center gap-2">
        <div className="shrink-0" style={{ width: size, height: size }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={COBRANZA_PIE}
                dataKey="valor"
                nameKey="nombre"
                cx="50%"
                cy="50%"
                innerRadius={compact ? 14 : 16}
                outerRadius={compact ? 24 : 28}
                paddingAngle={2}
                isAnimationActive={false}
              >
                {COBRANZA_PIE.map((entry) => (
                  <Cell key={entry.nombre} fill={entry.color} stroke="transparent" />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="min-w-0 space-y-1">
          {COBRANZA_PIE.map((item) => (
            <div key={item.nombre} className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
              <div className="min-w-0">
                <p className={`text-[10px] leading-none ${textMuted}`}>{item.nombre}</p>
                <p className={`text-[11px] font-semibold tabular-nums ${textPri}`}>{moneyShort(item.valor)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DashboardPreview({ modoOscuro, variant, idPrefix, className }) {
  const pad = layoutTokens(variant);
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const chipClass = modoOscuro ? 'bg-gray-800' : 'bg-gray-50';
  const panelClass = modoOscuro ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50';
  const divide = modoOscuro ? 'border-gray-700' : 'border-gray-200';
  const isPanel = variant === 'panel';
  const isSwap = variant === 'swap';
  const mesLabel = new Date().toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
  const chips = [
    { label: isPanel ? 'Vendido' : 'Ventas', value: DEMO.ventas, tone: 'default' },
    { label: 'Cobrado', value: DEMO.cobrado, tone: 'green' },
    { label: isPanel ? 'Pendiente' : 'Por cobrar', value: DEMO.pendiente, tone: 'amber' },
    { label: isPanel ? 'Egresos' : 'Gastos', value: DEMO.egresos, tone: 'red' },
  ];

  return (
    <PreviewShell modoOscuro={modoOscuro} variant={variant} className={className}>
      <div className={`flex items-center justify-between gap-3 border-b ${pad.header} ${divide}`}>
        <div className="min-w-0">
          <p className={`text-sm font-semibold truncate ${textPri}`}>
            Hola, <span className="text-[#1F80FF]">Ana</span>
          </p>
          <p className={`text-[11px] truncate ${textMuted}`}>Tu resumen de {mesLabel}</p>
        </div>
        <span className={`shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${modoOscuro ? 'bg-gray-800 text-gray-200 border-gray-600' : 'bg-gray-100 text-gray-700 border-gray-200'}`}>
          Plan Básico
        </span>
      </div>

      <div className={isPanel ? 'p-3 space-y-3' : pad.body}>
        {isPanel ? (
          <>
            <div>
              <p className={`text-[11px] font-semibold tracking-wide uppercase ${textMuted}`}>Flujo de caja del mes</p>
              <p className={`text-2xl font-semibold mt-0.5 tabular-nums ${toneClass('green', modoOscuro)}`}>
                {moneyShort(DEMO.flujo)}
              </p>
              <p className={`text-[11px] mt-0.5 ${textMuted}`}>Cobrado − Egresos</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
              {chips.map((chip) => (
                <div key={chip.label} className={`px-2 py-1.5 rounded-lg text-center ${chipClass}`}>
                  <p className={`text-[10px] uppercase ${textMuted}`}>{chip.label}</p>
                  <p className={`text-xs font-semibold tabular-nums ${toneClass(chip.tone, modoOscuro)}`}>{moneyShort(chip.value)}</p>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className={`rounded-xl border p-3.5 ${panelClass}`}>
            <p className={`text-[11px] font-semibold tracking-wide uppercase ${textMuted}`}>Flujo de caja del mes</p>
            <p className={`${isSwap ? 'text-xl' : 'text-2xl'} font-semibold mt-0.5 tabular-nums ${toneClass('green', modoOscuro)}`}>
              {formatMoney(DEMO.flujo)}
            </p>
            <p className={`text-[11px] mt-0.5 ${textMuted}`}>Cobrado − Egresos</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mt-2.5">
              {chips.map((chip) => (
                <div key={chip.label} className={`px-2 py-1.5 rounded-lg text-center ${chipClass}`}>
                  <p className={`text-[10px] uppercase ${textMuted}`}>{chip.label}</p>
                  <p className={`text-xs font-semibold tabular-nums ${toneClass(chip.tone, modoOscuro)}`}>{moneyShort(chip.value)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={`rounded-xl border p-2.5 ${panelClass}`}>
          <div className="flex items-center justify-between mb-0.5">
            <p className={`text-xs font-semibold ${textPri}`}>Evolución mensual</p>
            <p className={`text-[10px] ${textMuted}`}>Últimos 6 meses</p>
          </div>
          <Sparkline
            data={EVOLUCION}
            height={isPanel ? 68 : isSwap ? 72 : 88}
            gradientId={`${idPrefix}-dash`}
            modoOscuro={modoOscuro}
          />
        </div>

        {!isPanel && (
          <div className="grid grid-cols-2 gap-1.5">
            <PieCobranza modoOscuro={modoOscuro} textPri={textPri} textMuted={textMuted} panelClass={panelClass} compact={isSwap} />
            <div className={`rounded-xl border p-2.5 ${panelClass}`}>
              <p className={`text-xs font-semibold ${textPri}`}>vs mes anterior</p>
              <p className={`text-base font-bold tabular-nums mt-1 ${textPri}`}>{moneyShort(DEMO.flujo)}</p>
              <div className="inline-flex items-center gap-1 mt-1.5 px-2 py-1 rounded-lg bg-green-500/15">
                <span className="text-xs font-semibold text-green-500">+12,4%</span>
                <span className={`text-[10px] ${textMuted}`}>vs mes ant.</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </PreviewShell>
  );
}

function CobrarPreview({ modoOscuro, variant, className }) {
  const pad = layoutTokens(variant);
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const divide = modoOscuro ? 'border-gray-700' : 'border-gray-200';

  return (
    <PreviewShell modoOscuro={modoOscuro} variant={variant} className={className}>
      <div className={`border-b space-y-2.5 ${pad.header} ${divide}`}>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${modoOscuro ? 'bg-[#1F80FF]/20 text-[#5CA3FF]' : 'bg-[#1F80FF]/10 text-[#004AAD]'}`}>
            Cobrar
          </span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${modoOscuro ? 'bg-red-900/50 text-red-200' : 'bg-red-50 text-red-700'}`}>
            {DEMO.promesasVencidas} promesa(s) vencida(s)
          </span>
        </div>
        <div>
          <p className={`text-sm font-semibold tabular-nums ${textPri}`}>
            {formatMoney(DEMO.pendiente)} pendientes
            <span className={`font-normal ${textMuted}`}> · {DEMO.impagas} impaga(s) · {DEMO.vencidas} vencida(s)</span>
          </p>
          <p className={`text-[11px] mt-0.5 ${textMuted}`}>Priorizá impagas y ejecutá acciones de cobro</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {['Todas', 'Vencidas', '1–30 días', '31–60 días'].map((label, i) => (
            <span
              key={label}
              className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${
                i === 1
                  ? modoOscuro ? 'border-[#1F80FF] bg-[#1F80FF]/20 text-[#5CA3FF]' : 'border-[#1F80FF] bg-[#1F80FF]/10 text-[#004AAD]'
                  : modoOscuro ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-600'
              }`}
            >
              {label}
            </span>
          ))}
        </div>
      </div>

      <div className={`divide-y ${divide}`}>
        {COBRAR_ROWS.map((row) => (
          <div key={row.numero} className={pad.row}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold ${textPri}`}>N°{row.numero}</span>
                  <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${agingBadgeClass(row.tone, modoOscuro)}`}>
                    {row.aging}
                  </span>
                </div>
                <p className={`text-xs font-medium mt-0.5 truncate ${textPri}`}>{row.cliente}</p>
              </div>
              <p className={`text-sm font-bold tabular-nums shrink-0 ${textPri}`}>{moneyShort(row.saldo)}</p>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {['WhatsApp', 'Email', 'Link MP'].map((action, i) => (
                <span
                  key={action}
                  className={`px-2 py-0.5 rounded-md text-[9px] font-semibold border ${
                    i === 0
                      ? 'bg-[#1F80FF] text-white border-[#1F80FF]'
                      : modoOscuro ? 'border-gray-600 text-gray-300' : 'border-gray-200 text-gray-600'
                  }`}
                >
                  {action}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className={`border-t text-[10px] ${pad.footer} ${divide} ${textMuted}`}>
        Mostrando 3 de {DEMO.impagas} · total {moneyShort(DEMO.pendiente)}
      </div>
    </PreviewShell>
  );
}

function AnalisisPreview({ modoOscuro, variant, idPrefix, className }) {
  const pad = layoutTokens(variant);
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const divide = modoOscuro ? 'border-gray-700' : 'border-gray-200';
  const gridBg = modoOscuro ? 'bg-gray-800' : 'bg-gray-100';
  const compact = variant === 'swap' || variant === 'panel';
  const agingTotal = AGING_BUCKETS.reduce((sum, b) => sum + b.monto, 0);

  return (
    <PreviewShell modoOscuro={modoOscuro} variant={variant} className={className}>
      <div className={`border-b ${pad.header} ${divide}`}>
        <p className={`text-sm font-medium tabular-nums ${textPri}`}>
          Tasa {DEMO.tasaCobro}% en el período
          <span className={`text-xs font-normal ${modoOscuro ? 'text-green-300' : 'text-green-700'}`}> (+6 pp vs anterior)</span>
          <span className={`text-xs font-normal ${textMuted}`}> · {DEMO.vencidas} vencida(s) hoy</span>
        </p>
        <p className={`text-[11px] mt-0.5 ${textMuted}`}>Análisis de cobranza · Reportes</p>
      </div>

      <div className={`grid grid-cols-2 gap-px ${gridBg}`}>
        {[
          { label: 'Tasa de cobranza', value: `${DEMO.tasaCobro}%`, hint: `${moneyShort(DEMO.cobrado)} cobrado`, tone: 'green', highlight: true },
          { label: 'Pendiente actual', value: moneyShort(DEMO.pendiente), hint: `${DEMO.impagas} comprobante(s)`, tone: 'amber' },
          { label: 'Vencidas hoy', value: String(DEMO.vencidas), hint: 'Fuera de plazo', tone: 'red' },
          { label: 'DSO cartera', value: `${DEMO.dso} días`, hint: 'Días de venta en cartera', tone: 'default' },
        ].map((kpi) => (
          <div
            key={kpi.label}
            className={`${pad.kpi} ${kpi.highlight ? (modoOscuro ? 'bg-[#1F80FF]/10' : 'bg-[#1F80FF]/5') : (modoOscuro ? 'bg-gray-900' : 'bg-white')}`}
          >
            <p className={`text-[10px] font-semibold tracking-wide uppercase ${textMuted}`}>{kpi.label}</p>
            <p className={`text-sm font-semibold tabular-nums ${toneClass(kpi.tone, modoOscuro)}`}>{kpi.value}</p>
            <p className={`text-[10px] mt-0.5 ${textMuted}`}>{kpi.hint}</p>
          </div>
        ))}
      </div>

      <div className={`border-t ${pad.section} ${divide}`}>
        <p className={`text-xs font-semibold ${textPri}`}>Últimos 6 meses</p>
        <p className={`text-[10px] ${textMuted}`}>Tasa de cobro histórica</p>
        <Sparkline data={COBRANZA_TREND} height={compact ? 64 : 72} gradientId={`${idPrefix}-analisis`} modoOscuro={modoOscuro} />
      </div>

      <div className={`border-t ${pad.section} pb-4 ${divide}`}>
        <p className={`text-xs font-semibold ${textPri}`}>Aging de cartera</p>
        <p className={`text-[10px] mb-2.5 ${textMuted}`}>{DEMO.impagas} comprobante(s) · {moneyShort(agingTotal)}</p>
        <div className={`flex h-2 rounded-full overflow-hidden mb-2.5 ${modoOscuro ? 'bg-gray-800' : 'bg-gray-100'}`}>
          {AGING_BUCKETS.map((bucket) => (
            <div
              key={bucket.label}
              className={bucket.color}
              style={{ width: `${(bucket.monto / agingTotal) * 100}%` }}
            />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {AGING_BUCKETS.map((bucket) => (
            <div key={bucket.label} className={`rounded-lg border px-2 py-1.5 ${modoOscuro ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
              <p className={`text-[9px] ${textMuted}`}>{bucket.label}</p>
              <p className={`text-xs font-bold tabular-nums ${textPri}`}>{bucket.count}</p>
              <p className={`text-[9px] tabular-nums ${textMuted}`}>{moneyShort(bucket.monto)}</p>
            </div>
          ))}
        </div>
      </div>
    </PreviewShell>
  );
}

function ContadorPreview({ modoOscuro, variant, className }) {
  const pad = layoutTokens(variant);
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const divide = modoOscuro ? 'border-gray-700' : 'border-gray-200';
  const gridBg = modoOscuro ? 'bg-gray-800' : 'bg-gray-100';

  return (
    <PreviewShell modoOscuro={modoOscuro} variant={variant} className={className}>
      <div className={`border-b ${pad.header} ${divide}`}>
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${modoOscuro ? 'bg-gray-800 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
            Vista contador
          </span>
          <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${modoOscuro ? 'bg-sky-900/50 text-sky-200' : 'bg-sky-50 text-sky-800'}`}>
            Solo lectura
          </span>
        </div>
        <p className={`text-sm font-semibold ${textPri}`}>Distribuidora Ana</p>
        <p className={`text-[11px] ${textMuted}`}>Resumen de septiembre · flujo, proyección y pack</p>
      </div>

      <div className={`border-b ${divide}`}>
        <div className={`${pad.section} border-b flex items-center justify-between ${divide}`}>
          <p className={`text-xs font-semibold ${textPri}`}>Flujo del mes</p>
          <p className={`text-[10px] ${textMuted}`}>Cobrado − Egresos</p>
        </div>
        <div className={`grid grid-cols-2 gap-px ${gridBg}`}>
          {[
            { label: 'Cobrado', value: moneyShort(DEMO.cobrado), hint: '12 ventas', tone: 'green' },
            { label: 'Pendiente', value: moneyShort(DEMO.pendiente), tone: 'amber' },
            { label: 'Egresos', value: moneyShort(DEMO.egresos), hint: '8 gastos', tone: 'red' },
            { label: 'Flujo neto', value: moneyShort(DEMO.flujo), hint: 'Superávit del mes', tone: 'green', highlight: true },
          ].map((kpi) => (
            <div
              key={kpi.label}
              className={`${pad.kpi} ${kpi.highlight ? (modoOscuro ? 'bg-[#1F80FF]/10' : 'bg-[#1F80FF]/5') : (modoOscuro ? 'bg-gray-900' : 'bg-white')}`}
            >
              <p className={`text-[10px] font-semibold tracking-wide uppercase ${textMuted}`}>{kpi.label}</p>
              <p className={`text-sm font-semibold tabular-nums ${toneClass(kpi.tone, modoOscuro)}`}>{kpi.value}</p>
              {kpi.hint && <p className={`text-[10px] ${textMuted}`}>{kpi.hint}</p>}
            </div>
          ))}
        </div>
      </div>

      <div className={`border-b ${pad.section} ${divide}`}>
        <p className={`text-xs font-semibold ${textPri}`}>Flujo proyectado · 3 meses</p>
        <p className={`text-[10px] mb-1.5 ${textMuted}`}>Cobranza histórica {DEMO.tasaCobro}%</p>
        <div className={`rounded-lg border overflow-hidden ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className={`grid grid-cols-4 gap-px text-[9px] font-semibold uppercase tracking-wide ${gridBg} ${textMuted}`}>
            <div className={`px-2 py-1 ${modoOscuro ? 'bg-gray-900' : 'bg-white'}`}>Mes</div>
            <div className={`px-2 py-1 text-right ${modoOscuro ? 'bg-gray-900' : 'bg-white'}`}>Ing.</div>
            <div className={`px-2 py-1 text-right ${modoOscuro ? 'bg-gray-900' : 'bg-white'}`}>Egr.</div>
            <div className={`px-2 py-1 text-right ${modoOscuro ? 'bg-gray-900' : 'bg-white'}`}>Flujo</div>
          </div>
          {PROYECCION.map((row) => (
            <div key={row.mes} className={`grid grid-cols-4 gap-px border-t ${divide} text-[10px]`}>
              <div className={`px-2 py-1.5 font-medium ${textPri} ${modoOscuro ? 'bg-gray-900' : 'bg-white'}`}>{row.mes}</div>
              <div className={`px-2 py-1.5 text-right tabular-nums ${toneClass('green', modoOscuro)} ${modoOscuro ? 'bg-gray-900' : 'bg-white'}`}>{moneyShort(row.ingresos)}</div>
              <div className={`px-2 py-1.5 text-right tabular-nums ${toneClass('red', modoOscuro)} ${modoOscuro ? 'bg-gray-900' : 'bg-white'}`}>{moneyShort(row.egresos)}</div>
              <div className={`px-2 py-1.5 text-right font-semibold tabular-nums ${toneClass('green', modoOscuro)} ${modoOscuro ? 'bg-gray-900' : 'bg-white'}`}>{moneyShort(row.flujo)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className={`${pad.section} flex items-center justify-between gap-3`}>
        <div>
          <p className={`text-xs font-semibold ${textPri}`}>Pack contador (ZIP)</p>
          <p className={`text-[10px] ${textMuted}`}>Ventas, egresos, impagas y estado de resultados</p>
        </div>
        <span className="shrink-0 px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-[#1F80FF] text-white">
          Descargar
        </span>
      </div>
    </PreviewShell>
  );
}

export default function ProductPreview({
  variant = 'full',
  preset = 'dashboard',
  className = '',
  idPrefix = 'preview',
}) {
  const { modoOscuro } = useContext(ThemeContext);
  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';

  if (variant === 'compact') {
    const mainLabel = preset === 'cobrar' ? 'Por cobrar' : preset === 'analisis' ? 'Tasa de cobro' : preset === 'contador' ? 'Flujo neto' : 'Flujo de caja';
    const mainValue = preset === 'analisis' ? `${DEMO.tasaCobro}%` : moneyShort(preset === 'cobrar' ? DEMO.pendiente : preset === 'contador' ? DEMO.flujo : DEMO.flujo);
    const mainTone = preset === 'cobrar' ? 'amber' : 'green';
    const badge = preset === 'cobrar' ? 'Cobrar' : preset === 'analisis' ? 'Reportes' : preset === 'contador' ? 'Lector' : 'Plan Básico';
    const chartData = preset === 'analisis' ? COBRANZA_TREND : EVOLUCION;

    return (
      <SpotlightCard spotlightColor="transparent" className={`${box} rounded-2xl border shadow-lg overflow-hidden p-3 ${className}`}>
        <div className="flex items-end justify-between gap-3 mb-2">
          <div className="min-w-0">
            <p className={`text-[10px] font-semibold tracking-wide uppercase ${textMuted}`}>{mainLabel}</p>
            <p className={`text-xl font-semibold tabular-nums ${toneClass(mainTone, modoOscuro)}`}>{mainValue}</p>
          </div>
          <span className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full border ${modoOscuro ? 'bg-gray-800 text-gray-200 border-gray-600' : 'bg-gray-100 text-gray-700 border-gray-200'}`}>
            {badge}
          </span>
        </div>
        <Sparkline data={chartData} height={72} gradientId={`${idPrefix}-compact`} modoOscuro={modoOscuro} />
      </SpotlightCard>
    );
  }

  const shared = { modoOscuro, variant, idPrefix, className };

  if (variant === 'panel' || preset === 'dashboard') {
    return <DashboardPreview {...shared} preset={preset} />;
  }
  if (preset === 'cobrar') return <CobrarPreview {...shared} />;
  if (preset === 'analisis') return <AnalisisPreview {...shared} />;
  if (preset === 'contador') return <ContadorPreview {...shared} />;
  return <DashboardPreview {...shared} />;
}
